// src/components/profile/modals/ChangeEmailModal.jsx
import React, { useState } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { changeEmail } from '../../../services/profile.service';
import useModalHistory from '../../../hooks/useModalHistory';

const ChangeEmailModal = ({ show, onHide, token, userEmail, showAlert, onSuccess }) => {
  // Form fields state
  const [newEmail, setNewEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [showOtp, setShowOtp] = useState(false);
  
  // UI state
  const [loading, setLoading] = useState(false);

    useModalHistory(show, onHide, 'email-change-modal');
  
  // Handle form reset when modal is closed
  const handleClose = () => {
    resetForm();
    onHide();
  };
  
  // Reset form fields
  const resetForm = () => {
    setNewEmail('');
    setConfirmEmail('');
    setOtp('');
    setShowOtp(false);
    setLoading(false);
  };
  
  // Check if emails match and are valid
  const areEmailsValid = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(newEmail) && newEmail === confirmEmail;
  };
  
  // Handle email change process
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!showOtp) {
      // Validation for initial step
      if (!areEmailsValid()) {
        showAlert('Please ensure both email addresses match and are valid', 'danger');
        return;
      }
      
      // Check if new email is the same as current email
      if (newEmail === userEmail) {
        showAlert('New email must be different from your current email', 'danger');
        return;
      }
    } else {
      // Validation for OTP step
      if (!otp.trim()) {
        showAlert('Please enter the OTP sent to your email', 'danger');
        return;
      }
    }
    
    setLoading(true);
    
    try {
      if (!showOtp) {
        // Initiate email change - request OTP
        const result = await changeEmail(newEmail, confirmEmail, null, token);
        
        if (result.success) {
          showAlert('OTP sent to your new email address', 'success');
          setShowOtp(true);
        } else {
          throw new Error(result.message || 'Failed to initiate email change');
        }
      } else {
        // Verify OTP and complete email change
        const result = await changeEmail(newEmail, confirmEmail, otp, token);
        
        if (result.success) {
          showAlert('Email address changed successfully', 'success');
          
          // Update user details in session storage
          const userDetails = JSON.parse(sessionStorage.getItem('userDetails'));
          if (userDetails) {
            userDetails.Email = newEmail;
            sessionStorage.setItem('userDetails', JSON.stringify(userDetails));
          }
          
          // Callback to parent component to refresh data
          if (onSuccess) {
            onSuccess();
          }
          
          handleClose();
        } else {
          throw new Error(result.message || 'Failed to verify OTP');
        }
      }
    } catch (error) {
      console.error('Error changing email:', error);
      showAlert(error.message || 'An error occurred while changing email', 'danger');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Modal 
      show={show} 
      onHide={handleClose}
      centered
    >
      <Modal.Header className="bg-dark text-white">
        <Modal.Title>Change Email Address</Modal.Title>
        <Button 
          variant="link" 
          className="close text-white" 
          onClick={handleClose}
        >
          <span aria-hidden="true">&times;</span>
        </Button>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          {!showOtp ? (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Current Email</Form.Label>
                <Form.Control
                  type="email"
                  value={userEmail || ''}
                  disabled
                  readOnly
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>New Email Address</Form.Label>
                <Form.Control
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="Enter new email address"
                  required
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Confirm New Email Address</Form.Label>
                <Form.Control
                  type="email"
                  value={confirmEmail}
                  onChange={(e) => setConfirmEmail(e.target.value)}
                  placeholder="Confirm new email address"
                  required
                  isInvalid={confirmEmail && newEmail !== confirmEmail}
                />
                {confirmEmail && newEmail !== confirmEmail && (
                  <Form.Control.Feedback type="invalid">
                    Email addresses do not match
                  </Form.Control.Feedback>
                )}
              </Form.Group>
              
              <div className="alert alert-info">
                <i className="fas fa-info-circle mr-2"></i>
                A verification code will be sent to your new email address. You'll need this code to complete the change.
              </div>
            </>
          ) : (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Verification Code (OTP)</Form.Label>
                <Form.Control
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="Enter verification code"
                  required
                />
                <Form.Text className="text-white">
                  Enter the verification code sent to {newEmail}
                </Form.Text>
              </Form.Group>
              
              <div className="d-flex justify-content-between mb-3">
                <Button 
                  variant="link" 
                  onClick={() => setShowOtp(false)}
                  disabled={loading}
                >
                  <i className="fas fa-arrow-left mr-1"></i> Back
                </Button>
                
                <Button 
                  variant="link" 
                  onClick={async () => {
                    setLoading(true);
                    try {
                      const result = await changeEmail(newEmail, confirmEmail, null, token);
                      if (result.success) {
                        showAlert('New verification code sent', 'success');
                      } else {
                        throw new Error(result.message || 'Failed to resend verification code');
                      }
                    } catch (error) {
                      showAlert(error.message || 'Failed to resend verification code', 'danger');
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                >
                  Resend Code
                </Button>
              </div>
            </>
          )}
          
          <Button 
            variant="success" 
            type="submit"
            className="w-100"
            disabled={loading || (!showOtp && !areEmailsValid())}
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="mr-2"
                />
                {showOtp ? 'Verifying...' : 'Sending Code...'}
              </>
            ) : (
              showOtp ? 'Verify and Change Email' : 'Send Verification Code'
            )}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default ChangeEmailModal;